<template>
  <section class="main">
    <loader v-show="!isListLoaded || !isNewsLoaded"></loader>
    <div v-show="isNewsLoaded" class="news">
      <div class="news__item">
        <h1 class="news__heading">{{ current.title }}</h1>
        <span class="news__date">{{ formatDate(current.added * 1000) }} </span>
        <html-with-links
          tag="p"
          class="news__text"
          :html="current.text"
        ></html-with-links>
      </div>
      <div class="pagination">
        <router-link
          :to="`/news/${id - 1}`"
          class="pagination__link"
          :class="{ pagination__link_disabled: id == 1 }"
          ><img
            class="pagination__icon pagination__icon_rotate"
            :src="require(`@/assets/themes/${BRAND_KEY}/images/icon-chevron.svg`)"
          />{{ $t("PREVIOUS_NEWS") }}</router-link
        >
        <router-link
          :to="`/news/${+id + 1}`"
          class="pagination__link"
          :class="{
            pagination__link_disabled: !news.length || id == news[0].id,
          }"
          >{{$t('NEXT_NEWS')}}<img class="pagination__icon"
            :src="require(`@/assets/themes/${BRAND_KEY}/images/icon-chevron.svg`)"
        /></router-link>
      </div>
    </div>
    <aside v-show="isListLoaded" class="sidebar">
      <div class="list">
        <h2 class="list__heading">
          <img class="list__icon" src="@/assets/icon-news-sidebar.svg" />{{
            $t("NEWS_HISTORY")
          }}
        </h2>
        <ul class="list__links">
          <li v-for="item in news" :key="item.id">
            <router-link
              class="list__link"
              :class="{ list__link_active: item.id == id }"
              :to="`/news/${item.id}`"
              >{{ item.title }}</router-link
            >
          </li>
        </ul>
      </div>
    </aside>
  </section>
</template>

<script>
import Loader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks';

export default {
  components: { Loader, HtmlWithLinks },
  data() {
    return {
      isListLoaded: false,
      isNewsLoaded: false,
      news: [],
      current: {},
    };
  },
  computed: {
    id() {
      return (this.$route.params && this.$route.params.id) || (this.news[0] && this.news[0].id);
    },
    title() {
      return this.current.title;
    },
  },
  methods: {
    getCurrent() {
      if (!+this.id) {
        this.$router.go(-1);
        return null;
      }

      this.isNewsLoaded = false;
      return this.$api.getNews({ id: this.id }).then((data) => {
        this.current = data;
        this.isNewsLoaded = true;
      });
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    getData() {
      return this.$api.getNews({
        limit: 999,
      }).then((data) => {
        this.news = data;
        this.isListLoaded = true;

        return this.getCurrent();
      });
    },
  },
  watch: {
    id() {
      this.getCurrent();
    },
  },
  serverPrefetch() {
    return this.getData();
  },
  created() {
    this.getData();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

a {
  word-break: break-all;
}

.main {
  display: flex;

  @include notebook {
    flex-direction: column;
  }
}

.news {
  flex-grow: 1;

  &__item {
    background-image: var(--news-article-gradient);
    border-radius: 10px;
  }

  &__heading {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 20px;
    padding: 10px;
    border-bottom: 1px solid #e5e5e5;
  }

  &__date {
    display: block;
    color: $text-secondary;
    font-size: 12px;
    padding: 10px 10px 0;
  }

  &__text {
    padding: 10px;
    line-height: 17px;

    &::v-deep p {
      margin: 5px 0;
    }

    &::v-deep em {
      font-style: italic;
    }

    &::v-deep {

      blockquote {
        font-style: italic;
        margin-left: 15px;
        text-decoration: underline;
        text-decoration-style: dotted;
      }
      ol {
        list-style: decimal inside;
      }

      ul {
        list-style: disc inside;
      }
    }
  }

  &__separator {
    content: "";
    display: block;
    height: 15px;
  }
}

.pagination {
  margin-top: 15px;

  &__icon {
    &_rotate {
      transform: rotate(180deg);
    }
  }

  &__link {
    color: var(--text-tertiary);
    font-weight: 600;

    &_disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    &:first-child {
      margin-right: 25px;

      .pagination__icon {
        margin-right: 5px;
      }
    }

    &:last-child {
      .pagination__icon {
        margin-left: 5px;
      }
    }
  }
}

.sidebar {
  width: 400px;
  flex: 0 0 400px;
  margin-left: 20px;

  @include notebook {
    width: 100%;
    flex: 0 0 100%;
    margin-left: 0;
    margin-top: 15px;
  }
}

.list {
  background-image: var(--news-sidebar-gradient);
  border-radius: 10px;

  &__heading {
    display: flex;
    align-items: center;
    color: var(--text-primary);
    font-size: 20px;
    font-weight: 600;
    padding: 15px;

    @include notebook {
      padding: 10px;
    }
  }

  &__links {
    max-height: 315px;
    overflow: scroll;

    @include notebook {
      max-height: 240px;
    }
  }

  &__icon {
    margin-right: 15px;

    @include notebook {
      margin-right: 10px;
    }
  }

  &__link {
    display: block;
    padding: 7px 15px;
    font-weight: 400;
    word-break: break-word;

    @include notebook {
      padding: 7px 10px;
    }

    &.router-link-exact-active,
    &_active {
      background: var(--news-active-color);
      color: var(--text-tertiary);
    }
  }
}
</style>
